import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { getCategoryColor, getTagColor, getStatusColor, getFeedbackTypeColor, getSourceColor, TAGS } from '../utils/constants';
import supabase from '../lib/supabase';

const { FiArrowLeft, FiPlus, FiSearch, FiHelpCircle, FiBookOpen, FiMessageSquare, FiThumbsUp, FiLoader, FiAlertTriangle } = FiIcons;

const ModulePage = ({ category, onNavigate }) => {
  const [activeTab, setActiveTab] = useState('questions');
  const [searchTerm, setSearchTerm] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [userUpvotes, setUserUpvotes] = useState(new Set());

  // Real data states
  const [questions, setQuestions] = useState([]);
  const [learnings, setLearnings] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchModuleData();
    loadUserVotes();
  }, [category]);

  const fetchModuleData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch questions for this category
      const { data: questionsData, error: questionsError } = await supabase
        .from('questions_kb2024')
        .select('id, title, category, status, tags, asked_by, created_at')
        .eq('category', category)
        .order('created_at', { ascending: false });

      if (questionsError) throw questionsError;

      // Fetch learnings for this category
      const { data: learningsData, error: learningsError } = await supabase
        .from('learnings_kb2024')
        .select('id, title, category, tags, shared_by, created_at')
        .eq('category', category)
        .order('created_at', { ascending: false });

      if (learningsError) throw learningsError;

      // Fetch feedback for this category
      const { data: feedbackData, error: feedbackError } = await supabase
        .from('feedback_kb2024')
        .select('id, title, description, category, feedback_type, source, author, created_at, upvotes')
        .eq('category', category)
        .order('created_at', { ascending: false });

      if (feedbackError) throw feedbackError;

      setQuestions(questionsData || []);
      setLearnings(learningsData || []);
      setFeedback(feedbackData || []);
    } catch (error) {
      console.error('Error fetching module data:', error);
      setError('Unable to load module data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load user's votes from localStorage
  const loadUserVotes = () => {
    try {
      const savedVotes = localStorage.getItem('pal-ng-feedback-votes');
      if (savedVotes) {
        setUserUpvotes(new Set(JSON.parse(savedVotes)));
      }
    } catch (error) {
      console.error('Error loading user votes:', error);
    }
  };

  // Save user's votes to localStorage
  const saveUserVotes = (votes) => {
    try {
      localStorage.setItem('pal-ng-feedback-votes', JSON.stringify([...votes]));
    } catch (error) {
      console.error('Error saving user votes:', error);
    }
  };

  const getFilteredData = () => {
    let data;
    switch (activeTab) {
      case 'questions':
        data = questions;
        break;
      case 'learnings':
        data = learnings;
        break;
      case 'feedback':
        data = feedback;
        break;
      default:
        data = [];
    }

    return data.filter(item => {
      const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesTag = !tagFilter || (item.tags && item.tags.includes(tagFilter));

      return matchesSearch && matchesTag;
    });
  };

  const filteredData = getFilteredData();

  const handleUpvote = async (feedbackId) => {
    try {
      const newUpvotes = new Set(userUpvotes);
      const isCurrentlyUpvoted = newUpvotes.has(feedbackId);
      const currentFeedback = feedback.find(item => item.id === feedbackId);
      
      if (!currentFeedback) return;

      let newUpvoteCount;
      
      if (isCurrentlyUpvoted) {
        // Remove upvote
        newUpvotes.delete(feedbackId);
        newUpvoteCount = Math.max(0, (currentFeedback.upvotes || 0) - 1);
      } else {
        // Add upvote
        newUpvotes.add(feedbackId);
        newUpvoteCount = (currentFeedback.upvotes || 0) + 1;
      }

      // Update local state immediately for better UX
      setUserUpvotes(newUpvotes);
      saveUserVotes(newUpvotes);

      // Update local feedback state
      const updatedFeedback = feedback.map(item => {
        if (item.id === feedbackId) {
          return { ...item, upvotes: newUpvoteCount };
        }
        return item;
      });
      setFeedback(updatedFeedback);

      // Update database
      const { error } = await supabase
        .from('feedback_kb2024')
        .update({ upvotes: newUpvoteCount })
        .eq('id', feedbackId);

      if (error) {
        console.error('Error updating upvotes in database:', error);
        // Revert local changes if database update fails
        const revertedUpvotes = new Set(userUpvotes);
        if (isCurrentlyUpvoted) {
          revertedUpvotes.add(feedbackId);
        } else {
          revertedUpvotes.delete(feedbackId);
        }
        setUserUpvotes(revertedUpvotes);
        saveUserVotes(revertedUpvotes);
        
        const revertedFeedback = feedback.map(item => {
          if (item.id === feedbackId) {
            return { ...item, upvotes: currentFeedback.upvotes };
          }
          return item;
        });
        setFeedback(revertedFeedback);
      }
    } catch (error) {
      console.error('Error handling upvote:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const truncateText = (text, maxLength = 150) => {
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiLoader} className="w-8 h-8 text-blue-600 animate-spin" />
            <span className="text-lg text-gray-600">Loading {category} module...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <button
          onClick={() => onNavigate('dashboard')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors duration-200"
        >
          <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
          Back to Dashboard
        </button>

        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{category} Module</h1>
            <p className="text-gray-600">Questions, learnings, and feedback for {category}</p>
          </div>
          <span className={`px-4 py-2 text-sm font-medium rounded-full ${getCategoryColor(category)}`}>
            {category}
          </span>
        </div>
      </motion.div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6"
        >
          <p className="text-red-700">{error}</p>
          <button
            onClick={fetchModuleData}
            className="mt-2 text-red-600 hover:text-red-700 font-medium"
          >
            Try Again
          </button>
        </motion.div>
      )}

      {/* Search and Filter */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8"
      >
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search questions, learnings, and feedback..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div>
            <select
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Tags</option>
              {TAGS.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          </div>
        </div>
      </motion.div>

      {/* Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8"
      >
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('questions')}
            className={`flex items-center gap-3 px-6 py-4 font-medium transition-colors duration-200 border-b-2 ${activeTab === 'questions'
              ? 'border-blue-500 text-blue-600 bg-blue-50'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
          >
            <SafeIcon icon={FiHelpCircle} className="w-5 h-5" />
            Questions
            <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
              {questions.length}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('learnings')}
            className={`flex items-center gap-3 px-6 py-4 font-medium transition-colors duration-200 border-b-2 ${activeTab === 'learnings'
              ? 'border-green-500 text-green-600 bg-green-50'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
          >
            <SafeIcon icon={FiBookOpen} className="w-5 h-5" />
            Learnings
            <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
              {learnings.length}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('feedback')}
            className={`flex items-center gap-3 px-6 py-4 font-medium transition-colors duration-200 border-b-2 ${activeTab === 'feedback'
              ? 'border-orange-500 text-orange-600 bg-orange-50'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
          >
            <SafeIcon icon={FiMessageSquare} className="w-5 h-5" />
            Feedback
            <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
              {feedback.length}
            </span>
          </button>
        </div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="p-6 border-b border-gray-100"
        >
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => onNavigate('question-form', category)}
              className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
            >
              <SafeIcon icon={FiPlus} className="w-5 h-5" />
              Ask a Question in {category}
            </button>

            <button
              onClick={() => onNavigate('learning-form', category)}
              className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
            >
              <SafeIcon icon={FiPlus} className="w-5 h-5" />
              Share a Learning in {category}
            </button>

            <button
              onClick={() => onNavigate('feedback-form', category)}
              className="flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
            >
              <SafeIcon icon={FiPlus} className="w-5 h-5" />
              Submit Feedback for {category}
            </button>
          </div>
        </motion.div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'questions' && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              key="questions"
            >
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Questions ({filteredData.length})
                </h2>
                <p className="text-gray-600">Questions and discussions for {category}</p>
              </div>

              <div className="space-y-4">
                {filteredData.map((question, index) => (
                  <motion.div
                    key={question.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="p-4 border border-gray-100 rounded-lg hover:border-blue-200 transition-colors duration-200 cursor-pointer"
                    onClick={() => onNavigate('question-details', question.id)}
                  >
                    <h3 className="font-medium text-gray-900 mb-3 hover:text-blue-600 transition-colors">
                      {question.title}
                    </h3>

                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      {question.tags && question.tags.length > 0 && (
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTagColor(question.tags[0])}`}>
                          {question.tags[0]}
                        </span>
                      )}
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(question.status)}`}>
                        {question.status}
                      </span>
                    </div>

                    <div className="text-sm text-gray-600">
                      Asked by {question.asked_by} • {formatDate(question.created_at)}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {activeTab === 'learnings' && (
            <motion.div
              initial={{ opacity: 0, x: 0 }}
              animate={{ opacity: 1, x: 0 }}
              key="learnings"
            >
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Learnings ({filteredData.length})
                </h2>
                <p className="text-gray-600">Knowledge and insights shared for {category}</p>
              </div>

              <div className="space-y-4">
                {filteredData.map((learning, index) => (
                  <motion.div
                    key={learning.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="p-4 border border-gray-100 rounded-lg hover:border-green-200 transition-colors duration-200 cursor-pointer"
                    onClick={() => onNavigate('learning-details', learning.id)}
                  >
                    <h3 className="font-medium text-gray-900 mb-3 hover:text-green-600 transition-colors">
                      {learning.title}
                    </h3>

                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      {learning.tags && learning.tags.length > 0 && (
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTagColor(learning.tags[0])}`}>
                          {learning.tags[0]}
                        </span>
                      )}
                    </div>

                    <div className="text-sm text-gray-600">
                      Shared by {learning.shared_by} • {formatDate(learning.created_at)}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {activeTab === 'feedback' && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              key="feedback"
            >
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Feedback ({filteredData.length})
                </h2>
                <p className="text-gray-600">Community feedback and suggestions for {category}</p>
              </div>

              <div className="space-y-6">
                {filteredData.map((feedbackItem, index) => (
                  <motion.div
                    key={feedbackItem.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="border border-gray-200 rounded-lg p-6 hover:border-orange-200 hover:shadow-md transition-all duration-200"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 hover:text-orange-600 transition-colors">
                        {feedbackItem.title}
                      </h3>

                      <button
                        onClick={() => handleUpvote(feedbackItem.id)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200 ${userUpvotes.has(feedbackItem.id)
                          ? 'bg-orange-100 border-orange-300 text-orange-700'
                          : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                          }`}
                      >
                        <SafeIcon icon={FiThumbsUp} className="w-4 h-4" />
                        <span className="font-medium">
                          {feedbackItem.upvotes || 0}
                        </span>
                      </button>
                    </div>

                    <p className="text-gray-700 mb-4 leading-relaxed">
                      {truncateText(feedbackItem.description)}
                    </p>

                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getFeedbackTypeColor(feedbackItem.feedback_type)}`}>
                        {feedbackItem.feedback_type}
                      </span>
                      {feedbackItem.source && (
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSourceColor(feedbackItem.source)}`}>
                          {feedbackItem.source}
                        </span>
                      )}
                    </div>

                    <div className="text-sm text-gray-600">
                      Submitted by <span className="font-medium">{feedbackItem.author}</span> • {formatDate(feedbackItem.created_at)}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* No Results State */}
          {filteredData.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-center py-12"
            >
              <SafeIcon
                icon={activeTab === 'questions' ? FiHelpCircle : activeTab === 'learnings' ? FiBookOpen : FiMessageSquare}
                className="w-16 h-16 text-gray-300 mx-auto mb-4"
              />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No {activeTab} found</h3>
              <p className="text-gray-600">
                {searchTerm || tagFilter
                  ? `No ${activeTab} found for the current filters in ${category}.`
                  : `No ${activeTab} have been posted in ${category} yet. Be the first to contribute!`
                }
              </p>
            </motion.div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ModulePage;